import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { customStorage } from '@/lib/hooks/zustand-stores/utils';
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store';
import {
  AppName,
  TOGGLEABLE_APPS,
  EnabledAppsState,
  DEFAULT_ENABLED_APPS,
  ChatAppKeys,
  ChatAppsState,
  DEFAULT_CHAT_APPS
} from '@/lib/types/app-settings';

// Lowercase keys for easier reference
export const enum WebsiteAppKeys {
  match = 'Match',
  people = 'People',
  googleDrive = 'Google Drive',
  microsoftDrive = 'Microsoft Drive',
  peopleRecommendations = 'peopleRecommendations'
}

// Chat apps specific types
type ChatApp = ChatAppKeys;

export interface App {
  id: string;
  name: string;
  icon: string;
  description: string;
  enabled: boolean;
  href: string;
}

interface AppsState {
  apps: App[];
  isAppsListOpen: boolean;
  setAppsListOpen: (open: boolean) => void;
  toggleAppsList: () => void;
}

type State = {
  websiteApps: EnabledAppsState;
  chatApps: ChatAppsState;
  initialized: boolean;
  apps: App[];
  isAppsListOpen: boolean;
  isAgentMode: boolean;
}

type Actions = {
  // Website level actions
  setWebsiteApps: (apps: EnabledAppsState) => void;
  updateWebsiteApp: (appName: AppName, enabled: boolean) => void;
  isWebsiteAppEnabled: (appName: AppName) => boolean;
  getAllWebsiteApps: () => EnabledAppsState;

  // Chat level actions
  setChatApps: (apps: ChatAppsState) => void;
  updateChatApp: (appName: ChatAppKeys, enabled: boolean) => void;
  isChatAppEnabled: (appName: ChatAppKeys) => boolean;
  getAllChatApps: () => ChatAppsState;

  // Agent mode actions
  setAgentMode: (enabled: boolean) => void;
  toggleAgentMode: () => void;

  // Initialization actions
  initializeWebsiteApps: () => void;
  initializeChatApps: () => void;

  // New actions for the new store
  setAppsListOpen: (open: boolean) => void;
  toggleAppsList: () => void;
}

type Store = State & Actions;

// Add helper function to determine if chat app should be enabled by default
const shouldEnableByDefault = (appKey: ChatAppKeys, websiteApps: EnabledAppsState): boolean => {
  // Only enable if both the default is true AND the corresponding website app is enabled
  const defaultEnabled = DEFAULT_CHAT_APPS[appKey];

  // Cast the chat app key to ToggleableApp since they share the same string values
  // This is safe because all ChatAppKeys correspond to toggleable website apps
  const websiteAppKey = appKey as unknown as keyof EnabledAppsState;
  const websiteAppEnabled = websiteApps[websiteAppKey] ?? false;
  return defaultEnabled && websiteAppEnabled;
};

const useAppsStore = create<Store>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state should be empty to allow proper hydration from storage
        websiteApps: {} as EnabledAppsState,
        chatApps: {} as ChatAppsState,
        initialized: false,
        apps: [],
        isAppsListOpen: false,
        isAgentMode: true, // Default to agent mode enabled

        // Website level actions
        setWebsiteApps: (apps) => set((state) => {
          state.websiteApps = apps;
        }),

        updateWebsiteApp: (appName, enabled) => set((state) => {
          if (TOGGLEABLE_APPS.includes(appName as any)) {
            state.websiteApps[appName as keyof EnabledAppsState] = enabled;
          }
        }),

        isWebsiteAppEnabled: (appName) => {
          const state = get();
          if (!TOGGLEABLE_APPS.includes(appName as any)) {
            return true; // Non-toggleable apps are always enabled
          }
          return state.websiteApps[appName as keyof EnabledAppsState] ?? DEFAULT_ENABLED_APPS[appName as keyof EnabledAppsState] ?? false;
        },

        getAllWebsiteApps: () => {
          return get().websiteApps;
        },

        // Chat level actions
        setChatApps: (apps) => set((state) => {
          state.chatApps = apps;
        }),

        updateChatApp: (appName, enabled) => set((state) => {
          if (appName in DEFAULT_CHAT_APPS) {
            state.chatApps[appName] = enabled;
          }
        }),

        isChatAppEnabled: (appName) => {
          const state = get();
          return state.chatApps[appName] ?? shouldEnableByDefault(appName, state.websiteApps) ?? false;
        },

        getAllChatApps: () => {
          return get().chatApps;
        },

        // Agent mode actions
        setAgentMode: (enabled) => set({ isAgentMode: enabled }),
        toggleAgentMode: () => set((state) => ({ isAgentMode: !state.isAgentMode })),

        // Initialize website apps from user settings
        initializeWebsiteApps: () => {
          const userSettings = useUserStore.getState().user?.settings;
          set((state) => {
            state.websiteApps = {
              ...DEFAULT_ENABLED_APPS,
              ...(userSettings?.enabledApps || {})
            };
            state.initialized = true;
          });
        },

        // Initialize chat apps from persisted state
        initializeChatApps: () => {
          const state = get();
          // If we have any persisted chat apps, keep them
          if (Object.keys(state.chatApps).length > 0) {
            // Ensure all default apps exist with their persisted or default values
            set((state) => {
              // For each chat app, check if it should be enabled by default
              const defaultValues = Object.keys(DEFAULT_CHAT_APPS).reduce((acc, key) => ({
                ...acc,
                [key]: true // All tools enabled by default for Agent Mode
              }), {});

              state.chatApps = {
                ...defaultValues,
                ...state.chatApps
              };
            });

          } else {
            // If no persisted state, initialize with properly computed defaults
            set((state) => {
              state.chatApps = Object.keys(DEFAULT_CHAT_APPS).reduce((acc, key) => ({
                ...acc,
                [key]: true // All tools enabled by default for Agent Mode
              }), {} as ChatAppsState);
            });
          }
        },

        // New actions for the new store
        setAppsListOpen: (open) => set({ isAppsListOpen: open }),
        toggleAppsList: () => set((state) => ({ isAppsListOpen: !state.isAppsListOpen }))
      })),
      {
        name: 'apps-storage',
        storage: createJSONStorage(() => customStorage as any),
        version: 1,
      }
    )
  )
);

export default useAppsStore;
